<template>
  <div class="main-content-container">
    <b-breadcrumb class="pb-2">
      <b-breadcrumb-item>
        <span
          @click="$router.push({ name: 'ViewPADirectory' })"
          class="text-uppercase font-family-medium"
        >
          Team Directory
        </span>
      </b-breadcrumb-item>
      <b-breadcrumb-item active>
        <span class="text-uppercase font-family-medium">Create EA Account</span>
      </b-breadcrumb-item>
    </b-breadcrumb>
    <h1
      class="font-lg primary-dark font-family-medium position-relative content-title"
    >
      Create EA Account
    </h1>
    <b-dropdown-divider class="ml-2 " />
    <ValidationObserver ref="createPA">
      <b-form
        @submit.prevent="sendInviteLink"
        @keypress.enter.prevent="sendInviteLink"
      >
        <b-form-group
          class="d-flex flex-column create-pa-form general-info-form-group align-center justify-content-center form-group-container"
          label="Fill the form below to create a new EA account"
        >
          <div class="mt-3" v-if="showError">
            <li v-for="error in errors" :key="error.message">
              <h6
                class="font-sm font-family-regular danger position-relative mb-3 error-1"
              >
                {{ getErrInSentenceCase(error) }}
              </h6>
            </li>
          </div>
          <div class="mt-3" v-if="success">
            <h6
              class="font-sm font-family-regular danger position-relative mb-3 error-1"
            >
              {{ getErrInSentenceCase(successMessage) }}
            </h6>
          </div>
          <b-card class="form-group-card d-flex flex-column create-pa-card">
            <div class="form-group-top d-flex mt-2">
              <ValidationProvider
                class="first-name-column"
                name="First Name field"
                rules="required"
                v-slot="{ errors }"
              >
                <b-form-group class="mt-2" label="First Name *">
                  <b-form-input
                    autocomplete="off"
                    size="lg"
                    type="text"
                    placeholder="First name"
                    class="mr-4"
                    v-model="firstName"
                  ></b-form-input>
                  <h6
                    v-if="errors[0]"
                    class="font-sm font-family-regular position-relative danger"
                  >
                    {{ errors[0] }}
                  </h6>
                </b-form-group>
              </ValidationProvider>
              <ValidationProvider
                name="Last Name"
                rules="required"
                v-slot="{ errors }"
              >
                <b-form-group class="mt-2 " label="Last Name *">
                  <b-form-input
                    autocomplete="off"
                    size="lg"
                    type="text"
                    placeholder="Last name"
                    v-model="lastName"
                  ></b-form-input>
                  <h6
                    v-if="errors[0]"
                    class="font-sm font-family-regular position-relative danger"
                  >
                    {{ errors[0] }}
                  </h6>
                </b-form-group>
              </ValidationProvider>
            </div>
            <ValidationProvider
              name="Email"
              rules="email|required"
              v-slot="{ errors }"
            >
              <b-form-group class="mt-4 mb-4" label="Email *">
                <b-form-input
                  autocomplete="off"
                  size="lg"
                  type="email"
                  placeholder="Email"
                  v-model="email"
                ></b-form-input>
                <h6
                  v-if="errors[0]"
                  class="font-sm font-family-regular position-relative danger"
                >
                  {{ errors[0] }}
                </h6>
              </b-form-group>
            </ValidationProvider>
            <b-row>
              <b-col>
                <ValidationProvider
                  name="Job Title"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <b-form-group class="mt-4" label="Job Title *">
                    <b-form-select
                      v-model="selectedJobTitle"
                      :options="teamJobTitles"
                    ></b-form-select>
                    <h6
                      v-if="errors[0]"
                      class="font-sm font-family-regular position-relative danger"
                    >
                      {{ errors[0] }}
                    </h6>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col>
                <ValidationProvider
                  name="Start Date"
                  rules="required|date"
                  v-slot="{ errors }"
                >
                  <b-form-group class="mt-4 mb-2" label="Start Date*">
                    <b-input-group>
                      <b-form-input
                        id="example-input"
                        placeholder="Start date"
                        v-model="dueDate"
                        autocomplete="off"
                      ></b-form-input>
                      <b-input-group-append>
                        <b-form-datepicker
                          v-model="dueDate"
                          button-only
                          :show-decade-nav="true"
                          right
                          class="datepicker"
                          locale="en-US"
                          aria-controls="example-input"
                        >
                          <template #button-content="">
                            <img src="@/assets/icons/date-picker.svg" alt="" />
                          </template>
                        </b-form-datepicker>
                      </b-input-group-append>
                    </b-input-group>
                    <h6
                      v-if="errors[0]"
                      class="font-sm font-family-regular position-relative danger"
                    >
                      {{ errors[0] }}
                    </h6>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
            <b-alert
              class="alert-btn-top alert-create-pa custom-alert mt-3"
              :show="alertDuration"
              v-if="showAlert"
              :fade="true"
              >{{ alertMsg }}</b-alert
            >
            <div
              class="d-flex align-items-center justify-content-center invite-btn-container"
            >
              <b-button
                @click="sendInviteLink"
                class="btn-pill invite-btn"
                :disabled="loading"
              >
                <b-spinner
                  style="width: 14px; height: 14px"
                  v-if="loading"
                  variant="light"
                  label="Spinning"
                ></b-spinner>
                {{ `${!loading ? "Create EA Account" : ""}` }}
              </b-button>
            </div>
          </b-card>
        </b-form-group>
      </b-form>
    </ValidationObserver>
  </div>
</template>

<script>
import { email, required } from "vee-validate/dist/rules";
import moment from "moment";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { extend } from "vee-validate";
import { mapActions } from "vuex";
import { TEAM_JOB_TITLES } from "@/utils/constants/index";

extend("email", email);
let dateMessage;
extend("date", {
  message: () => dateMessage,
  validate: value => {
    let mustContainTheseChars = /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
    let containsRequiredChars = mustContainTheseChars.test(value);
    if (containsRequiredChars) {
      return true;
    } else {
      dateMessage = "YYYY-MM-DD format required";
      return false;
    }
  }
});
extend("required", {
  ...required,
  message: "This is a required field"
});
export default {
  name: "CreatePAAccount",
  data() {
    return {
      successMessage: "",
      momentFrom: new Date(),
      firstName: "",
      fromMonth: "",
      lastName: "",
      email: "",
      loading: false,
      showError: false,
      success: false,
      alertMsg: "",
      variant: "",
      alertDuration: null,
      dueDate: "",
      min: moment.utc().format("YYYY-MM-DD"),
      showAlert: false,
      errors: [],
      selectedJobTitle: null,
      teamJobTitles: []
    };
  },
  components: {
    ValidationProvider,
    ValidationObserver
  },
  methods: {
    async sendInviteLink() {
      let valid = await this.$refs.createPA.validate();
      if (valid) {
        this.showError = false;
        this.success = false;
        const data = {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email.toLowerCase(),
          jobTitle: this.selectedJobTitle,
          startDate: this.dueDate
        };
        this.loading = true;
        this.actionSendInviteLink(data)
          .then(res => {
            this.success = true;
            this.firstName = "";
            this.lastName = "";
            this.email = "";
            this.selectedJobTitle = null;
            this.dueDate = null;
            this.$refs.createPA.reset();
            this.successMessage = res.data.detail;
            this.redirectToEAList();
          })
          .catch(err => {
            this.loading = false;
            this.showError = true;
            if (err.response.status === 401) {
              this.errors = [
                "User token expired. Please logout and login again."
              ];
            } else {
              this.errors = [...err.response.data.email];
            }
          });
      }
    },
    getErrInSentenceCase(error) {
      return error.charAt(0).toUpperCase() + error.slice(1);
    },
    redirectToEAList() {
      setTimeout(
        () =>
          this.$router.push({
            name: "ViewPADirectory"
          }),
        2000
      );
    },
    getTeamJobTitles() {
      this.teamJobTitles = [...TEAM_JOB_TITLES];
      this.teamJobTitles.unshift({ text: "Select Job Title", value: null });
    },
    ...mapActions("authModule", ["actionSendInviteLink"])
  },
  created() {
    this.getTeamJobTitles();
  }
};
</script>

<style scoped lang="scss">
.first-name-column {
  margin-right: 2.5rem;
}
.create-pa-card {
  margin-bottom: 5rem;
}
.form-group-container {
  max-width: 41.5rem !important;
  margin: 7rem auto 0 auto;
}
.form-group-card {
  padding: 1rem 1.8rem 0 1.8rem;
}
.invite-btn-container {
  margin-top: 2rem;
  margin-bottom: 1.5rem;
}
.invite-btn {
  width: 20.8rem;
  padding: 0.6rem 0;
}
</style>
